import { useEffect, useRef } from 'react';
import { useSWRConfig } from 'swr';
import { useLocale } from '@hooks/useLocale';
import { primeQuickAddProductCache } from './useVariantSelection';

/**
 * Eagerly warms the Quick Add SWR cache for a bounded, known-small set of
 * product codes — e.g. `ImageHotspot`'s ≤6 hotspots per tab, whose
 * image/price/name are already being fetched via `useHitSwatches`. Unlike
 * the PLP grid (up to 52 unvirtualized cards, where eager-fetching every
 * card's variant data would be wasteful and is instead warmed lazily via
 * `useVariantSelection`'s own hover-intent `prefetch()`), a handful of
 * hotspot products is a small enough cost to preload unconditionally, so a
 * shopper who opens a hotspot and taps "Add to Cart" rarely sees a loading
 * state at all — `useVariantSelection`'s `useSWR` call reads this
 * already-warmed cache entry synchronously once it mounts.
 *
 * Fire-and-forget: writes to the shared SWR cache, nothing is returned.
 */
export function usePrefetchQuickAddProducts(productCodes: string[]): void {
  const locale = useLocale();
  const { mutate } = useSWRConfig();
  const prefetchedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!locale) return;
    productCodes.forEach((code) => {
      if (!code || prefetchedRef.current.has(code)) return;
      prefetchedRef.current.add(code);
      void primeQuickAddProductCache(mutate, locale, code);
    });
  }, [locale, mutate, productCodes]);
}

export default usePrefetchQuickAddProducts;
